<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'

import BetaSeoTag from '@/components/beta-dashboard/molecules/BetaSeoTag.vue'
import { useSiteStore } from '@/stores/site'

interface BetaSeoDraft {
    userEmail: string
    activeSegment: string
    activeSubsegments: unknown[]
    userArtisticName: string
    siteLanguage: string
    siteDescription: string
    h1: string
    document: string
    location: unknown
    cookiebar: boolean
    blockCopyImage: boolean
}

const siteStore = useSiteStore()

const saving = ref(false)
const creatingTag = ref(false)
const removingTagIds = ref<number[]>([])
const tagName = ref('')
const initialized = ref(false)
const baseline = ref('')

const draft = reactive<BetaSeoDraft>({
    userEmail: '',
    activeSegment: '',
    activeSubsegments: [],
    userArtisticName: '',
    siteLanguage: 'pt-br',
    siteDescription: '',
    h1: '',
    document: '',
    location: null,
    cookiebar: false,
    blockCopyImage: false,
})

const cloneValue = <T>(value: T): T => {
    if (value === null || value === undefined) return value

    return JSON.parse(JSON.stringify(value)) as T
}

const snapshot = () => JSON.stringify(draft)

const resetDraft = () => {
    Object.assign(draft, {
        userEmail: String(siteStore.mainEmail || ''),
        activeSegment: String(siteStore.segment?.segment || ''),
        activeSubsegments: cloneValue(siteStore.segment?.subSegments ?? []),
        userArtisticName: String(siteStore.name || ''),
        siteLanguage: String(siteStore.language || 'pt-br'),
        siteDescription: String(siteStore.siteDescription || ''),
        h1: String(siteStore.seoText || ''),
        document: String(siteStore.documentId || ''),
        location: cloneValue(siteStore.location ?? null),
        cookiebar: Boolean(siteStore.cookiebar),
        blockCopyImage: Boolean(siteStore.blockCopyImage),
    })

    initialized.value = true
    baseline.value = snapshot()
}

watch(
    () => siteStore.defined,
    (defined) => {
        if (defined && !initialized.value) resetDraft()
    },
    { immediate: true },
)

const tags = computed<Array<{ id: number; name: string }>>(() => (
    Array.isArray(siteStore.tags) ? siteStore.tags : []
))

const dirty = computed(() => initialized.value && snapshot() !== baseline.value)
const canCreateTag = computed(() => Boolean(tagName.value.trim()) && !creatingTag.value)

const createTag = async () => {
    const name = tagName.value.trim()
    if (!name || creatingTag.value) return

    creatingTag.value = true
    try {
        const created = await siteStore.handleCreateTag(name)
        if (created) tagName.value = ''
    } finally {
        creatingTag.value = false
    }
}

const removeTag = async (tagId: number) => {
    if (removingTagIds.value.includes(tagId)) return

    removingTagIds.value = [...removingTagIds.value, tagId]
    try {
        await siteStore.handleDeleteTag(tagId)
    } finally {
        removingTagIds.value = removingTagIds.value.filter((id) => id !== tagId)
    }
}

const saveSeo = async () => {
    if (!dirty.value || saving.value) return

    saving.value = true
    try {
        const saved = await siteStore.handleUpdateSeo(cloneValue(draft))
        if (saved) baseline.value = snapshot()
    } finally {
        saving.value = false
    }
}
</script>

<template>
    <form class="beta-seo" @submit.prevent="saveSeo">
        <div class="beta-seo__heading">
            <div>
                <p>SEO</p>
                <h2>Busca e informações do site</h2>
                <span>Organize os dados usados pelo navegador e pelos mecanismos de busca.</span>
            </div>
            <small>Otimização do site</small>
        </div>

        <section class="beta-seo__section">
            <div class="beta-seo__section-heading">
                <h3>Informações principais</h3>
                <p>Defina como o site será identificado nos resultados e no navegador.</p>
            </div>

            <div class="beta-seo__fields">
                <label class="beta-seo__field">
                    <span>Título do site</span>
                    <input v-model="draft.userArtisticName" type="text" maxlength="100">
                    <small>Nome comercial exibido depois do título de cada página.</small>
                </label>

                <label class="beta-seo__field">
                    <span>Idioma do site</span>
                    <select v-model="draft.siteLanguage">
                        <option value="pt-br">Português (Brasil)</option>
                        <option value="pt-pt">Português (Portugal)</option>
                        <option value="en">Inglês</option>
                        <option value="es">Espanhol</option>
                        <option value="it">Italiano</option>
                    </select>
                    <small>Idioma principal informado aos buscadores.</small>
                </label>

                <label class="beta-seo__field is-wide">
                    <span>Segmento</span>
                    <input v-model="draft.activeSegment" type="text" maxlength="120">
                    <small>Sua principal área de atuação, como fotografia ou arquitetura.</small>
                </label>

                <label class="beta-seo__field is-wide">
                    <span>Descrição do site</span>
                    <textarea v-model="draft.siteDescription" rows="3" maxlength="300"></textarea>
                    <small>Resumo exibido abaixo do nome do site nos resultados de busca.</small>
                </label>

                <label class="beta-seo__field is-wide">
                    <span>Texto do H1</span>
                    <input v-model="draft.h1" type="text" maxlength="255">
                    <small>Frase principal da Home com atividade, localização e nome da marca.</small>
                </label>
            </div>
        </section>

        <section class="beta-seo__section">
            <div class="beta-seo__section-heading">
                <h3>Palavras-chave</h3>
                <p>Adicione cidades, serviços, produtos e termos relacionados ao seu trabalho.</p>
            </div>

            <div class="beta-seo__tag-entry">
                <input
                    v-model="tagName"
                    type="text"
                    maxlength="80"
                    placeholder="Ex.: fotografia de casamento"
                    @keydown.enter.prevent="createTag"
                >
                <button type="button" :disabled="!canCreateTag" @click="createTag">
                    {{ creatingTag ? 'Adicionando…' : 'Adicionar tag' }}
                </button>
            </div>

            <div v-if="tags.length" class="beta-seo__tags">
                <BetaSeoTag
                    v-for="tag in tags"
                    :key="tag.id"
                    :name="tag.name"
                    :removing="removingTagIds.includes(tag.id)"
                    @remove="removeTag(tag.id)"
                />
            </div>
            <p v-else class="beta-seo__empty-tags">Nenhuma tag adicionada.</p>

            <aside class="beta-seo__tip">
                Pense como seu cliente pesquisaria. O
                <a href="https://trends.google.com" target="_blank" rel="noopener noreferrer">Google Trends</a>
                ajuda a comparar termos de busca.
            </aside>
        </section>

        <section class="beta-seo__section">
            <div class="beta-seo__section-heading">
                <h3>Configurações do site</h3>
                <p>Comportamentos adicionais preservados do painel clássico.</p>
            </div>

            <div class="beta-seo__options">
                <label>
                    <input v-model="draft.cookiebar" type="checkbox">
                    <span>
                        <strong>Barra informativa de cookies</strong>
                        <small>Exibe o aviso de cookies aos visitantes.</small>
                    </span>
                </label>

                <label>
                    <input v-model="draft.blockCopyImage" type="checkbox">
                    <span>
                        <strong>Inibir cópia de imagem</strong>
                        <small>Dificulta ações comuns de cópia nas imagens do site.</small>
                    </span>
                </label>
            </div>

            <label class="beta-seo__field beta-seo__document">
                <span>CNPJ</span>
                <input v-model="draft.document" type="text" maxlength="30">
            </label>
        </section>

        <div class="beta-seo__actions">
            <button type="submit" :disabled="!dirty || saving">
                {{ saving ? 'Salvando…' : 'Salvar SEO' }}
            </button>
        </div>
    </form>
</template>

<style scoped>
.beta-seo {
    display: grid;
    gap: 16px;
}

.beta-seo__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 17px;
    border-bottom: 1px solid #edf1f6;
}

.beta-seo__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-seo__heading h2 {
    margin-top: 3px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 850;
    letter-spacing: -0.025em;
}

.beta-seo__heading span {
    display: block;
    margin-top: 5px;
    color: #7d8da3;
    font-size: 11px;
}

.beta-seo__heading > small {
    flex: none;
    border-radius: 999px;
    background: color-mix(in srgb, var(--main-color) 8%, #f1f4f8);
    padding: 6px 9px;
    color: var(--main-color);
    font-size: 10px;
    font-weight: 800;
}

.beta-seo__section {
    border: 1px solid #e6ebf2;
    border-radius: 18px;
    background: #f9fbfd;
    padding: 15px;
}

.beta-seo__section-heading h3 {
    color: #263d5a;
    font-size: 14px;
    font-weight: 850;
}

.beta-seo__section-heading p {
    margin-top: 3px;
    color: #8190a4;
    font-size: 12px;
}

.beta-seo__fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 13px;
}

.beta-seo__field {
    display: grid;
    align-content: start;
    gap: 6px;
}

.beta-seo__field.is-wide {
    grid-column: 1 / -1;
}

.beta-seo__field > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 850;
}

.beta-seo__field > small {
    color: #8795a8;
    font-size: 10px;
    line-height: 1.4;
}

.beta-seo__field input,
.beta-seo__field select,
.beta-seo__field textarea,
.beta-seo__tag-entry input {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 12px;
    background: #fff;
    padding: 10px 11px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-seo__field input:focus,
.beta-seo__field select:focus,
.beta-seo__field textarea:focus,
.beta-seo__tag-entry input:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-seo__field textarea {
    resize: vertical;
}

.beta-seo__tag-entry {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    margin-top: 12px;
}

.beta-seo__tag-entry button,
.beta-seo__actions button {
    min-height: 39px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 15px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-seo__tag-entry button:disabled,
.beta-seo__actions button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.beta-seo__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 11px;
}

.beta-seo__empty-tags {
    margin-top: 11px;
    color: #8997aa;
    font-size: 10px;
}

.beta-seo__tip {
    margin-top: 12px;
    border-radius: 12px;
    background: #fff;
    padding: 10px 12px;
    color: #73839a;
    font-size: 10px;
    line-height: 1.5;
}

.beta-seo__tip a {
    color: var(--main-color);
    font-weight: 800;
}

.beta-seo__options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
    margin-top: 12px;
}

.beta-seo__options label {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: start;
    gap: 9px;
    border: 1px solid #e2e8f0;
    border-radius: 13px;
    background: #fff;
    padding: 10px;
    cursor: pointer;
}

.beta-seo__options input {
    margin-top: 2px;
    accent-color: var(--main-color);
}

.beta-seo__options strong,
.beta-seo__options small {
    display: block;
}

.beta-seo__options strong {
    color: #344c68;
    font-size: 12px;
    font-weight: 850;
}

.beta-seo__options small {
    margin-top: 2px;
    color: #8795a8;
    font-size: 10px;
    line-height: 1.4;
}

.beta-seo__document {
    max-width: 360px;
    margin-top: 12px;
}

.beta-seo__actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 1px;
}

@media (max-width: 700px) {
    .beta-seo__heading {
        flex-direction: column;
    }

    .beta-seo__fields,
    .beta-seo__options {
        grid-template-columns: 1fr;
    }

    .beta-seo__tag-entry {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 479px) {
    .beta-seo__section {
        border-radius: 15px;
        padding: 12px;
    }
}
</style>
